// Opportunity table sorting. The server returns rows already ranked by the ACTIVE entry price
// mode (Opportunity DESC); the user may re-sort the visible rows by any column. Missing values
// always sort last, whatever the direction.

export const MODES = ["LTP", "OFFER"];

// column key -> value accessor
export const COLUMN_VALUE = {
  rank: (r) => r.rank,
  underlying: (r) => r.underlying,
  underlying_ltp: (r) => r.underlying_ltp,
  expiry: (r) => r.expiry,
  ce_strike: (r) => r.ce?.strike,
  ce_ltp: (r) => r.ce?.ltp,
  ce_bid: (r) => r.ce?.bid,
  ce_bid_qty: (r) => r.ce?.bid_qty,
  ce_ask: (r) => r.ce?.ask,
  ce_ask_qty: (r) => r.ce?.ask_qty,
  ce_oi: (r) => r.ce?.oi,
  pe_strike: (r) => r.pe?.strike,
  pe_ltp: (r) => r.pe?.ltp,
  pe_bid: (r) => r.pe?.bid,
  pe_bid_qty: (r) => r.pe?.bid_qty,
  pe_ask: (r) => r.pe?.ask,
  pe_ask_qty: (r) => r.pe?.ask_qty,
  pe_oi: (r) => r.pe?.oi,
  strike_diff: (r) => r.strike_diff,
  ltp_combined: (r) => r.LTP?.combined,
  ltp_opp: (r) => (r.LTP?.qualified ? r.LTP.opp : null),
  ltp_opp_lot: (r) => (r.LTP?.qualified ? r.LTP.opp_lot : null),
  offer_combined: (r) => r.OFFER?.combined,
  offer_opp: (r) => (r.OFFER?.qualified ? r.OFFER.opp : null),
  offer_opp_lot: (r) => (r.OFFER?.qualified ? r.OFFER.opp_lot : null),
  lot_size: (r) => r.lot_size,
  multiplier: (r) => r.economics?.economic_multiplier,
  // valuation (backend option_valuation.py)
  ce_fv: (r) => r.valuation?.ce_val?.fair_value,
  pe_fv: (r) => r.valuation?.pe_val?.fair_value,
  fv_sum: (r) => r.valuation?.fair_value_sum,
  fv_opp: (r) => r.valuation?.fair_value_opportunity,
  expiry_pct: (r) => r.valuation?.expiry_profit_pct,
  expiry_lot: (r) => r.valuation?.expiry_profit_lot,
  one_day_pct: (r) => r.valuation?.one_day_profit_pct,
  one_day_lot: (r) => r.valuation?.one_day_profit_lot,
  one_day_decay_lot: (r) => r.valuation?.one_day_decay_profit_lot,
  one_day_decay_pct: (r) => r.valuation?.one_day_decay_profit_pct,
  actual_1d_lot: (r) => r.valuation?.actual_1d_profit_lot,
  funds_lot: (r) => r.valuation?.funds_per_lot,
  margin: (r) => r.capital?.capital_required,
  lots: (r) => r.valuation?.lots,
  roi: (r) => r.valuation?.potential_roi_pct,
  // Profit / Margin: the offer opportunity per lot over the broker's capital for one lot
  profit_margin: (r) => (r.OFFER?.qualified && r.OFFER.opp_lot !== null && r.OFFER.opp_lot !== undefined && r.capital?.capital_required
    ? r.OFFER.opp_lot / r.capital.capital_required : null),
  liquidity: (r) => r.valuation?.liquidity,
  time_left: (r) => r.valuation?.seconds_to_expiry,
};

/** Ranking choices offered above the Scanner (Offer Opportunity is the default and the executable one). */
export const RANKINGS = [
  ["offer_opp", "Offer Opportunity"], ["ltp_opp", "LTP Opportunity"], ["one_day_pct", "1-Day exec %"],
  ["expiry_pct", "Expiry Profit %"], ["profit_margin", "Profit / Margin"], ["liquidity", "Liquidity (min OI)"],
];

/** The default ordering: the active mode's Opportunity, descending. */
export const defaultSort = (mode) => ({ key: mode === "OFFER" ? "offer_opp" : "ltp_opp", dir: "desc" });

export function sortRows(rows, sort) {
  const get = COLUMN_VALUE[sort?.key];
  if (!get) return rows;
  const sign = sort.dir === "asc" ? 1 : -1;
  return rows
    .map((r, i) => [r, i])
    .sort(([a, ia], [b, ib]) => {
      const va = get(a), vb = get(b);
      const na = va === null || va === undefined, nb = vb === null || vb === undefined;
      if (na && nb) return ia - ib;
      if (na) return 1;
      if (nb) return -1;
      if (va < vb) return -1 * sign;
      if (va > vb) return 1 * sign;
      return ia - ib;                       // stable: keep the server's rank order on ties
    })
    .map(([r]) => r);
}

const DESC_FIRST = ["opp", "pct", "lot", "roi", "margin", "liquidity", "fv"];

export function nextSort(current, key) {
  if (current?.key !== key) return { key, dir: DESC_FIRST.some((x) => key.includes(x)) ? "desc" : "asc" };
  return { key, dir: current.dir === "desc" ? "asc" : "desc" };
}
